/** Lead → Deal conversion — v1 leads-list.js verbatim: the 32-field
 * required-info gate (no bypass), the Flip card (Transactions stage 0) and
 * the Wholesale card, the lead's stage='Deal' stamp, and 'Tom, Daniel' as
 * the default task owners. The deals/wholesale door sections have accepted
 * puts since Phases 5/9 — creation is pure ops. The ONE server-side piece,
 * POST /api/drive/transaction-folders, creates real Google Drive folders and
 * stays Phase B (v1 also creates folders lazily on first upload, so nothing
 * is lost by deferring). */
import { natKey } from "../../lib/natkey";
import { fmtMoneyEdit, parseMoneyEdit, type LeadFields } from "../edits/api";
import type { RecOp } from "../../lib/outbox";
import type { Lead } from "../../types";

// ---- the gate (v1 DEAL_REQUIRED_FIELDS + pVal, verbatim) ---------------------

export const DEAL_REQUIRED_FIELDS = [
  "address", "city_state_zip", "property_label", "source", "property_type", "bedrooms", "bathrooms",
  "sqft", "year_built", "living_sqft", "asking_price", "offer_price", "purchase_price", "arv",
  "roof_condition", "hvac_condition", "hwh_condition", "electrical_condition", "plumbing_condition",
  "foundation_details", "flood_zone", "sewer_septic", "water_source", "occupancy",
  "disposition_company", "disposition_name", "disposition_phone", "disposition_email",
  "title_company", "title_company_phone", "title_company_email", "repair_cost",
] as const;

export const FIELD_LABELS: Record<string, string> = {
  address: "Address", city_state_zip: "City / State / ZIP", property_label: "Property Label (Flip/Rental)",
  source: "Lead Source", property_type: "Property Type", bedrooms: "Bedrooms", bathrooms: "Bathrooms",
  sqft: "Square Feet", year_built: "Year Built", living_sqft: "Living Sqft", asking_price: "Asking Price",
  offer_price: "Offer Price", purchase_price: "Contract Price", arv: "ARV", roof_condition: "Roof Condition",
  hvac_condition: "HVAC Condition", hwh_condition: "Water Heater Condition", electrical_condition: "Electrical",
  plumbing_condition: "Plumbing", foundation_details: "Foundation", flood_zone: "Flood Zone",
  sewer_septic: "Sewer / Septic", water_source: "Water Source", occupancy: "Occupancy",
  disposition_company: "Disposition Company", disposition_name: "Disposition Contact Name",
  disposition_phone: "Disposition Phone", disposition_email: "Disposition Email", title_company: "Title Company",
  title_company_phone: "Title Company Phone", title_company_email: "Title Company Email",
  repair_cost: "Rehab / Repair Cost (> 0)",
};

const fs = (f: LeadFields | undefined, k: string): string => {
  const v = f?.[k];
  return typeof v === "string" && v.trim() ? v.trim() : "";
};

/** v1 pVal, adapted to v2's Lead + LEAD_EDITS fields. The auto-defaults are
 * v1's own (ptype 'Single Family', year '1999', flood 'X'). */
export function dealFieldValue(key: string, lead: Lead, f: LeadFields | undefined): unknown {
  switch (key) {
    case "address": return lead.address;
    case "city_state_zip": return (lead.address || "").split(",").length >= 3 ? "ok" : "";
    case "property_label": return fs(f, "label");
    case "source": return lead.source;
    case "property_type": return fs(f, "ptype") || "Single Family";
    case "bedrooms": return lead.beds;
    case "bathrooms": return lead.baths;
    case "sqft": return lead.sqft;
    case "year_built": return fs(f, "yearBuilt") || "1999";
    case "living_sqft": return lead.sqft;
    case "asking_price": return lead.price;
    case "offer_price": return parseMoneyEdit(f?.["offerPrice"]);
    case "purchase_price": return parseMoneyEdit(f?.["contractPrice"]);
    case "arv": return parseMoneyEdit(f?.["listedArv"]) ?? lead.arv; // v1: e.listedArv||p.arv
    case "roof_condition": return fs(f, "roofCond");
    case "hvac_condition": return fs(f, "hvacCond");
    case "hwh_condition": return fs(f, "whCond");
    case "electrical_condition": return fs(f, "elec");
    case "plumbing_condition": return fs(f, "plumb");
    case "foundation_details": return fs(f, "found");
    case "flood_zone": return fs(f, "flood") || "X";
    case "sewer_septic": return fs(f, "septic");
    case "water_source": return fs(f, "water");
    case "occupancy": return fs(f, "occupancy");
    case "disposition_company": return fs(f, "dispCompany");
    case "disposition_name": return fs(f, "dispName");
    case "disposition_phone": return fs(f, "dispPhone");
    case "disposition_email": return fs(f, "dispEmail");
    case "title_company": return fs(f, "titleCo");
    case "title_company_phone": return fs(f, "titlePhone");
    case "title_company_email": return fs(f, "titleEmail");
    case "repair_cost": return parseMoneyEdit(f?.["repairCost"]) ?? rehabCalcTotal(f);
    default: return undefined;
  }
}

const rehabCalcTotal = (f: LeadFields | undefined): number | null => {
  const rc = f?.["rehabCalc"];
  if (rc !== null && typeof rc === "object" && !Array.isArray(rc)) {
    const t = (rc as Record<string, unknown>)["total"];
    const n = parseMoneyEdit(t);
    if (n !== null) return n;
  }
  return null;
};

/** the keys still missing — empty array = the gate opens */
export function missingForDeal(lead: Lead, f: LeadFields | undefined): string[] {
  return DEAL_REQUIRED_FIELDS.filter((k) => {
    const v = dealFieldValue(k, lead, f);
    return v === undefined || v === null || v === "" || v === 0;
  });
}

// ---- the cards (v1 makeTransaction / makeWholesale, verbatim shapes) ---------

/** 'Manatee County' and 'Manatee' both → 'Manatee' */
export const shortCounty = (county: string | null): string =>
  (county ?? "").replace(/\s*county\s*$/i, "").trim();

const offerDisplay = (lead: Lead, f: LeadFields | undefined): string | null => {
  const offer = parseMoneyEdit(f?.["offerPrice"]);
  if (offer !== null) return fmtMoneyEdit(offer);
  return lead.price !== null ? fmtMoneyEdit(lead.price) : null;
};

export interface FlipCard extends Record<string, unknown> {
  a: string;
  sub: string;
  county: string;
  f: [string, string][];
  tD: number;
  tT: number;
  edits: Record<string, unknown>;
}

/** v1: card in TXN_COLS[0] with t_purchase seeded, ARV comped>listed>lead,
 * rehab carried, task owners defaulted to Tom & Daniel. */
export function flipCardFor(lead: Lead, f: LeadFields | undefined, owners = "Tom, Daniel"): FlipCard {
  const cty = shortCounty(lead.county);
  const price = offerDisplay(lead, f) ?? "—";
  const card: FlipCard = {
    a: lead.address,
    sub: (cty ? cty + " " : "") + "County",
    county: cty,
    f: [["Closing", "—"], ["Days to Closing", "—"], ["Purchase Price", price]],
    tD: 0,
    tT: 10,
    edits: { taskOwner: owners },
  };
  if (price !== "—") card.edits["t_purchase"] = price;
  // Carry the title company + dispo contact over (Tom 2026-08-31: "the title
  // information and dispo contact info isnt transferred to the transaction").
  // The gate above REQUIRED these on the lead and then the card dropped them.
  const carry: [string, string][] = [
    ["t_titleco", "titleCo"], ["t_titlephone", "titlePhone"], ["t_titleemail", "titleEmail"],
    ["t_dispco", "dispCompany"], ["t_dispname", "dispName"],
    ["t_dispphone", "dispPhone"], ["t_dispemail", "dispEmail"],
  ];
  for (const [dealKey, leadKey] of carry) {
    const v = fs(f, leadKey);
    if (v) card.edits[dealKey] = v;
  }
  const arv = parseMoneyEdit(f?.["compedArv"]) ?? parseMoneyEdit(f?.["listedArv"]) ?? lead.arv;
  if (arv !== null) card["arv"] = fmtMoneyEdit(arv);
  const rehab = parseMoneyEdit(f?.["repairCost"]) ?? rehabCalcTotal(f);
  if (rehab !== null) card["rehab"] = rehab;
  return card;
}

export interface WholesaleCard extends Record<string, unknown> {
  a: string;
  sub: string;
  seller: string;
  stage: string;
  ucp: string;
  buyer: string;
  fee: string;
  status: string;
}

export function wholesaleCardFor(lead: Lead, f: LeadFields | undefined): WholesaleCard {
  const sellerObj = f?.["seller"];
  const sellerName =
    sellerObj !== null && typeof sellerObj === "object" && !Array.isArray(sellerObj) && typeof (sellerObj as Record<string, unknown>)["name"] === "string"
      ? ((sellerObj as Record<string, unknown>)["name"] as string)
      : lead.seller ?? "Seller";
  return {
    a: lead.address,
    sub: shortCounty(lead.county) + " FL",
    seller: sellerName || "Seller",
    stage: "Under Contract",
    ucp: offerDisplay(lead, f) ?? "—",
    buyer: "—",
    fee: "—",
    status: "active",
  };
}

// ---- the ops -----------------------------------------------------------------

/** Flip: deals put at stage 0 (Contract Signed / Pending) — d IS the card
 * (rec_deals.data; edits decompose into fields). */
export function opCreateDeal(card: FlipCard): RecOp {
  return { s: "deals", op: "put", k: natKey(card.a), d: card, stage: 0 };
}

export function opCreateWholesale(w: WholesaleCard): RecOp {
  return { s: "wholesale", op: "put", k: natKey(w.a), d: w };
}

/** v1: p.stage='Deal' on the lead (qual untouched). */
export function opLeadStageDeal(address: string): RecOp {
  return { s: "lead_fields", op: "patch", k: natKey(address), d: { stage: "Deal" } };
}

export const CONVERT_MSG = {
  blockedTitle: "Can't convert to Deal yet",
  blocked: (n: number) =>
    `${n} required field${n > 1 ? "s" : ""} missing. Complete them in the sections above first — this stage is locked until everything is filled (no bypass).`,
  flipDone: "Flip deal created in Transactions",
  wholesaleDone: "Wholesale transaction created",
  folders: "Drive folders get created from v1 (or in the live-fire session).",
};
